import { useEffect, useMemo, useState } from "react";
import {
  Button,
  ButtonGroup,
  Modal,
  TextInput,
} from "@datapay-by-datacom/design-system";
import { Heading } from "react-aria-components";
import { UnsavedProgressConfirmModal } from "../../onboarding/components/UnsavedProgressConfirmModal";
import {
  ACCEPTED_ONBOARDING_DATE_FORMATS,
  parseOnboardingDate,
} from "../../onboarding/utils/onboardingDates";
import {
  createPayComponentRowDraft,
  isPayComponentRowDraftDirty,
  type PayComponentRowDraft,
} from "./payComponentRowEditorDraft";
import type { MappedRow } from "../utils/mappedRows";
import { RESERVED_LC } from "../utils/mappedRows";
import {
  END_DATE_HEADER_LC,
  START_DATE_HEADER_LC,
  resolveDateColumnHeader,
} from "../utils/dateColumns";
import styles from "./PayComponentRowEditModal.module.css";

interface PayComponentRowEditModalProps {
  isOpen: boolean;
  row: MappedRow | null;
  sourceRow: Record<string, string> | null;
  headers: readonly string[];
  componentColumn: string;
  /** Wizard session date-column overrides ("" = none). */
  startDateColumn?: string | null;
  endDateColumn?: string | null;
  rowErrors: readonly string[];
  isProcessing?: boolean;
  onCancel: () => void;
  onSave: (draft: PayComponentRowDraft) => void;
}

export function PayComponentRowEditModal({
  isOpen,
  row,
  sourceRow,
  headers,
  componentColumn,
  startDateColumn,
  endDateColumn,
  rowErrors,
  isProcessing = false,
  onCancel,
  onSave,
}: PayComponentRowEditModalProps) {
  const [draft, setDraft] = useState<PayComponentRowDraft | null>(null);
  const [discardOpen, setDiscardOpen] = useState(false);
  const [startDateError, setStartDateError] = useState<string | null>(null);
  const [endDateError, setEndDateError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen || !row || !sourceRow) {
      setDraft(null);
      setDiscardOpen(false);
      setStartDateError(null);
      setEndDateError(null);
      return;
    }
    setDraft(createPayComponentRowDraft(row, sourceRow, headers));
    setDiscardOpen(false);
    setStartDateError(null);
    setEndDateError(null);
  }, [isOpen, row, sourceRow, headers]);

  const original = useMemo(
    () =>
      row && sourceRow
        ? createPayComponentRowDraft(row, sourceRow, headers)
        : null,
    [row, sourceRow, headers],
  );
  const isDirty =
    draft && original ? isPayComponentRowDraftDirty(original, draft) : false;

  const headerList = [...headers];
  const startDateHeader = resolveDateColumnHeader(
    headerList,
    startDateColumn,
    START_DATE_HEADER_LC,
  );
  const endDateHeader = resolveDateColumnHeader(
    headerList,
    endDateColumn,
    END_DATE_HEADER_LC,
  );
  const canEditStartDate = startDateHeader != null;
  const canEditEndDate = endDateHeader != null;

  const dataColumns = headers.filter(
    (h) =>
      !RESERVED_LC.has(h.toLowerCase()) &&
      h !== componentColumn &&
      h !== startDateHeader &&
      h !== endDateHeader,
  );

  const handleCancel = () => {
    if (isDirty) {
      setDiscardOpen(true);
      return;
    }
    onCancel();
  };

  const handleSave = () => {
    if (!draft) return;

    const startResult = parseOnboardingDate(draft.startDate, "Start date");
    const endResult = parseOnboardingDate(draft.endDate, "End date");
    const nextStartError =
      "error" in startResult ? startResult.error : null;
    const nextEndError = "error" in endResult ? endResult.error : null;
    setStartDateError(nextStartError);
    setEndDateError(nextEndError);
    if (nextStartError || nextEndError) return;

    onSave({
      ...draft,
      startDate:
        "value" in startResult && startResult.value != null
          ? startResult.value
          : "",
      endDate:
        "value" in endResult && endResult.value != null
          ? endResult.value
          : "",
    });
  };

  if (!row || !draft) {
    return null;
  }

  return (
    <>
      <Modal isOpen={isOpen}>
        <div className={styles.modalContent}>
          <div className={styles.modalHeader}>
            <Heading slot="title">Edit row {row.index}</Heading>
          </div>

          <div className={styles.modalBody}>
            {rowErrors.length > 0 && (
              <div className={styles.issueSummary} role="alert">
                <strong>Issues on this row</strong>
                <ul className={styles.issueList}>
                  {rowErrors.map((message, index) => (
                    <li key={index}>{message}</li>
                  ))}
                </ul>
              </div>
            )}

            <div className={styles.fieldGrid}>
              <div className={styles.field}>
                <label htmlFor="pci-edit-employee-code">Employee code</label>
                <TextInput
                  id="pci-edit-employee-code"
                  value={draft.employeeCode}
                  onChange={(value) =>
                    setDraft((prev) =>
                      prev ? { ...prev, employeeCode: value } : prev,
                    )
                  }
                  isDisabled={isProcessing}
                />
              </div>
              <div className={styles.field}>
                <label htmlFor="pci-edit-employee-name">Employee name</label>
                <TextInput
                  id="pci-edit-employee-name"
                  value={draft.employeeName}
                  onChange={(value) =>
                    setDraft((prev) =>
                      prev ? { ...prev, employeeName: value } : prev,
                    )
                  }
                  isDisabled={isProcessing}
                />
              </div>
              <div className={styles.field}>
                <label htmlFor="pci-edit-component">Component</label>
                <TextInput
                  id="pci-edit-component"
                  value={draft.component}
                  onChange={(value) =>
                    setDraft((prev) =>
                      prev ? { ...prev, component: value } : prev,
                    )
                  }
                  isDisabled={isProcessing}
                />
              </div>
              <div className={styles.field}>
                <label htmlFor="pci-edit-start-date">Start date</label>
                <TextInput
                  id="pci-edit-start-date"
                  value={draft.startDate}
                  onChange={(value) => {
                    setStartDateError(null);
                    setDraft((prev) =>
                      prev ? { ...prev, startDate: value } : prev,
                    );
                  }}
                  isDisabled={isProcessing || !canEditStartDate}
                  isInvalid={Boolean(startDateError)}
                  aria-describedby={
                    startDateError
                      ? "pci-edit-start-date-hint pci-edit-start-date-error"
                      : "pci-edit-start-date-hint"
                  }
                />
                <p id="pci-edit-start-date-hint" className={styles.fieldHint}>
                  {ACCEPTED_ONBOARDING_DATE_FORMATS}
                </p>
                {startDateError ? (
                  <p
                    id="pci-edit-start-date-error"
                    className={styles.fieldError}
                    role="alert"
                  >
                    {startDateError}
                  </p>
                ) : null}
              </div>
              <div className={styles.field}>
                <label htmlFor="pci-edit-end-date">End date</label>
                <TextInput
                  id="pci-edit-end-date"
                  value={draft.endDate}
                  onChange={(value) => {
                    setEndDateError(null);
                    setDraft((prev) =>
                      prev ? { ...prev, endDate: value } : prev,
                    );
                  }}
                  isDisabled={isProcessing || !canEditEndDate}
                  isInvalid={Boolean(endDateError)}
                  aria-describedby={
                    endDateError
                      ? "pci-edit-end-date-hint pci-edit-end-date-error"
                      : "pci-edit-end-date-hint"
                  }
                />
                <p id="pci-edit-end-date-hint" className={styles.fieldHint}>
                  {ACCEPTED_ONBOARDING_DATE_FORMATS}
                </p>
                {endDateError ? (
                  <p
                    id="pci-edit-end-date-error"
                    className={styles.fieldError}
                    role="alert"
                  >
                    {endDateError}
                  </p>
                ) : null}
              </div>

              {dataColumns.length > 0 && (
                <>
                  <p className={styles.groupTitle}>Mapped column values</p>
                  {dataColumns.map((column) => (
                    <div key={column} className={styles.field}>
                      <label htmlFor={`pci-edit-col-${column}`}>{column}</label>
                      <input
                        id={`pci-edit-col-${column}`}
                        className={styles.textInput}
                        value={draft.columnValues[column] ?? ""}
                        disabled={isProcessing}
                        onChange={(e) =>
                          setDraft((prev) =>
                            prev
                              ? {
                                  ...prev,
                                  columnValues: {
                                    ...prev.columnValues,
                                    [column]: e.target.value,
                                  },
                                }
                              : prev,
                          )
                        }
                      />
                    </div>
                  ))}
                </>
              )}
            </div>
          </div>

          <div className={styles.modalFooter}>
            <ButtonGroup>
              <Button variant="tertiary" onPress={handleCancel} isDisabled={isProcessing}>
                Cancel
              </Button>
              <Button
                variant="primary"
                onPress={handleSave}
                isDisabled={!isDirty || isProcessing}
              >
                Save changes
              </Button>
            </ButtonGroup>
          </div>
        </div>
      </Modal>

      <UnsavedProgressConfirmModal
        isOpen={discardOpen}
        title="Discard row edits?"
        message="Unsaved changes to this row will be lost."
        onStay={() => setDiscardOpen(false)}
        onLeave={() => {
          setDiscardOpen(false);
          onCancel();
        }}
      />
    </>
  );
}
